import { useEffect, useRef, useState } from 'react';

/**
 * Count-up hook. Starts animating from 0 to `value` when the returned ref
 * enters the viewport (once). Duration ~1000ms with ease-out.
 * If value is not numeric, the raw value is returned untouched.
 */
export default function useCountUp(value, { duration = 1000, decimals = 0 } = {}) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(0);
  const numeric = typeof value === 'number' && !Number.isNaN(value);

  useEffect(() => {
    if (!numeric || !ref.current) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setDisplay(value); return; }

    let raf = 0;
    let start = 0;
    let done = false;

    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Number((eased * value).toFixed(decimals)));
      if (p < 1) raf = requestAnimationFrame(step);
      else done = true;
    };

    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting && !done) {
          raf = requestAnimationFrame(step);
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.3 });

    obs.observe(ref.current);
    return () => { obs.disconnect(); cancelAnimationFrame(raf); };
  }, [value, duration, decimals, numeric]);

  return { ref, display: numeric ? display : value };
}
